/**
 * mergeTokens — small "mail-merge" helper shared by SendQuoteModal /
 * SendReminderModal and (since 2026-04-19) the Quick Email flow that
 * hands drafts off to the planner's own email client via mailto.
 *
 * Two things live here:
 *   1. MERGE_TOKEN_CATALOG — list of available tokens shown in the
 *      picker UI, with human-readable labels + example values.
 *   2. buildTokenValues(ctx) + resolveMergeTokens(str, values) —
 *      build the substitution map and apply it. Missing values resolve
 *      to empty strings (we never want a literal {token} leaking into
 *      an outgoing email).
 *
 * Quick Email tokens are a superset of the original quote tokens;
 * every legacy token ({client_first_name} etc.) still resolves.
 *
 * Server-side parity: Netlify functions (send-quote.js, etc.) keep
 * their own copies of the resolver. If you add a token here, add the
 * matching resolver server-side too — but Quick Email NEVER hits a
 * server (mailto: only), so QE-only tokens don't need server parity.
 */

/**
 * Human-facing catalog used by the token picker.
 * `token` is the literal string inserted into the textarea.
 * `label` is the menu entry shown to the planner.
 * `example` is a tiny preview so the planner knows what they're picking.
 * `scope` (optional) hints where a token is most relevant so the
 * picker UI can filter by context (lead / quote / invoice / event).
 */
export const MERGE_TOKEN_CATALOG = [
  // Client / lead identity
  { token: '{client_first_name}', label: 'Client first name', example: 'Sarah',   scope: 'client' },
  { token: '{client_last_name}',  label: 'Client last name',  example: 'Chen',    scope: 'client' },
  { token: '{client_name}',       label: 'Client full name',  example: 'Sarah Chen', scope: 'client' },
  // Studio / planner identity
  { token: '{studio_name}',       label: 'Studio name',       example: 'Lumen Studio', scope: 'studio' },
  { token: '{planner_first_name}', label: 'Planner first name', example: 'Sarah', scope: 'studio' },
  { token: '{planner_name}',      label: 'Planner full name', example: 'Sarah Ellis', scope: 'studio' },
  { token: '{planner_email}',     label: 'Planner email',     example: 'sarah@elliscoevents.com', scope: 'studio' },
  { token: '{planner_phone}',     label: 'Planner phone',     example: '415.555.0912', scope: 'studio' },
  { token: '{calendar_link}',     label: 'Calendar link',     example: 'planos.app/c/sarah', scope: 'studio' },
  // Event
  { token: '{event_type}',        label: 'Event type',        example: 'wedding',  scope: 'event' },
  { token: '{event_date}',        label: 'Event date',        example: 'September 14, 2026', scope: 'event' },
  { token: '{days_until_event}',  label: 'Days until event',  example: '42',       scope: 'event' },
  { token: '{venue_name}',        label: 'Venue',             example: "Stags' Leap", scope: 'event' },
  { token: '{guest_count}',       label: 'Guest count',       example: '120',      scope: 'event' },
  // Money
  { token: '{total}',             label: 'Quote total',       example: '$4,200',   scope: 'quote' },
  { token: '{deposit_amount}',    label: 'Deposit amount',    example: '$1,260',   scope: 'quote' },
  { token: '{invoice_amount}',    label: 'Invoice amount',    example: '$3,240',   scope: 'invoice' },
  { token: '{amount_due}',        label: 'Amount due',        example: '$6,480',   scope: 'invoice' },
  { token: '{days_overdue}',      label: 'Days overdue',      example: '4',        scope: 'invoice' },
  // Links
  { token: '{quote_link}',        label: 'Quote link',        example: 'planos.app/q/abc123', scope: 'quote' },
  { token: '{contract_link}',     label: 'Contract link',     example: 'planos.app/c/abc123', scope: 'contract' },
  { token: '{invoice_link}',      label: 'Invoice link',      example: 'planos.app/i/abc123', scope: 'invoice' },
  { token: '{portal_link}',       label: 'Client portal link', example: 'planos.app/p/abc123', scope: 'portal' },
  { token: '{intake_link}',       label: 'Client intake link', example: 'planos.app/intake/abc123', scope: 'intake' },
  // Timing
  { token: '{days_since_inquiry}', label: 'Days since inquiry', example: '3',     scope: 'lead' },
  { token: '{due_date}',          label: 'Due date',          example: 'April 28, 2026', scope: 'invoice' },
]

/**
 * Build the substitution map from whatever context the caller has.
 * Every field is optional — missing values resolve to empty strings in
 * resolveMergeTokens so tokens never leak as literal text.
 *
 * Accepts a free-form `ctx` object; the most common shape is:
 *   { lead, profile, studioName, quoteTotal, eventDate,
 *     event, vendor, invoice, contract, quote, links }
 */
export function buildTokenValues(ctx = {}) {
  const {
    lead,
    profile,
    studioName,
    quoteTotal,
    eventDate,
    event,
    invoice,
    links,
  } = ctx

  // ── Client / lead identity ──────────────────────────────────────
  const firstName = lead?.contact_first_name || lead?.firstName || lead?.contactFirstName || ''
  const lastName  = lead?.contact_last_name  || lead?.lastName  || lead?.contactLastName  || ''
  const fullName  = [firstName, lastName].filter(Boolean).join(' ')
                    || lead?.couple_name
                    || lead?.client_name
                    || ''

  // ── Studio / planner identity ───────────────────────────────────
  const studio = studioName
    || profile?.business_name
    || profile?.studio_name
    || profile?.businessName
    || ''
  const plannerFull = profile?.full_name
    || profile?.name
    || [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
    || ''
  const plannerFirst = profile?.first_name
    || (plannerFull ? String(plannerFull).split(/\s+/)[0] : '')
  const plannerEmail = profile?.email || ''
  const plannerPhone = profile?.phone || ''
  const calendarLink = profile?.calendar_link || profile?.calendar_url || ''

  // ── Event ───────────────────────────────────────────────────────
  const eventType = event?.event_type || lead?.event_type || lead?.eventType || ''

  const rawDate = eventDate
    || event?.event_date
    || lead?.event_date
    || lead?.eventDate
    || null
  let prettyDate = ''
  let daysUntilEvent = ''
  if (rawDate) {
    const d = rawDate instanceof Date ? rawDate : new Date(rawDate)
    if (!isNaN(d.getTime())) {
      prettyDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      const diffMs = d.getTime() - Date.now()
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      if (Number.isFinite(days) && days > 0) daysUntilEvent = String(days)
    }
  }

  const venueName = event?.venue_name || event?.venue || lead?.venue || ''
  const guestCount = event?.guest_count || lead?.guest_count || ''

  // ── Money ────────────────────────────────────────────────────────
  const fmtMoney = (n) => typeof n === 'number'
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : ''

  const totalFmt = fmtMoney(quoteTotal)
  const depositFmt = fmtMoney(ctx?.depositAmount ?? ctx?.quote?.deposit_amount)
  const invoiceAmtFmt = fmtMoney(
    typeof invoice?.amount === 'number' ? invoice.amount
    : typeof invoice?.total === 'number' ? invoice.total
    : invoice?.total ? Number(invoice.total) : undefined
  )
  const amountDueFmt = fmtMoney(
    typeof invoice?.amount_due === 'number' ? invoice.amount_due
    : typeof invoice?.balance_due === 'number' ? invoice.balance_due
    : typeof invoice?.balance === 'number' ? invoice.balance
    : invoice?.balance_due ? Number(invoice.balance_due) : undefined
  )

  // days overdue
  let daysOverdue = ''
  const dueRaw = invoice?.due_date || invoice?.dueDate
  if (dueRaw) {
    const due = new Date(dueRaw)
    if (!isNaN(due.getTime())) {
      const diff = Math.floor((Date.now() - due.getTime()) / (1000 * 60 * 60 * 24))
      if (diff > 0) daysOverdue = String(diff)
    }
  }

  // ── Days since inquiry ──────────────────────────────────────────
  let daysSinceInquiry = ''
  const createdRaw = lead?.created_at || lead?.createdAt
  if (createdRaw) {
    const c = new Date(createdRaw)
    if (!isNaN(c.getTime())) {
      daysSinceInquiry = String(Math.max(0, Math.floor((Date.now() - c.getTime()) / (1000 * 60 * 60 * 24))))
    }
  }

  // ── Due date pretty ─────────────────────────────────────────────
  let dueDatePretty = ''
  if (dueRaw) {
    const due = new Date(dueRaw)
    if (!isNaN(due.getTime())) {
      dueDatePretty = due.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    }
  }

  // ── Links (caller provides; we don't fabricate URLs) ────────────
  const quoteLink    = links?.quote    || ctx?.quoteLink    || ''
  const contractLink = links?.contract || ctx?.contractLink || ''
  const invoiceLink  = links?.invoice  || ctx?.invoiceLink  || ''
  const portalLink   = links?.portal   || ctx?.portalLink   || ''
  const intakeLink   = links?.intake   || ctx?.intakeLink   || ''

  return {
    // client
    '{client_first_name}': firstName,
    '{client_last_name}':  lastName,
    '{client_name}':       fullName,
    // studio / planner
    '{studio_name}':        studio,
    '{planner_first_name}': plannerFirst,
    '{planner_name}':       plannerFull,
    '{planner_email}':      plannerEmail,
    '{planner_phone}':      plannerPhone,
    '{calendar_link}':      calendarLink,
    // event
    '{event_type}':       eventType,
    '{event_date}':       prettyDate,
    '{days_until_event}': daysUntilEvent,
    '{venue_name}':       venueName,
    '{guest_count}':      String(guestCount || ''),
    // money
    '{total}':           totalFmt,
    '{deposit_amount}':  depositFmt,
    '{invoice_amount}':  invoiceAmtFmt,
    '{amount_due}':      amountDueFmt,
    '{days_overdue}':    daysOverdue,
    // links
    '{quote_link}':    quoteLink,
    '{contract_link}': contractLink,
    '{invoice_link}':  invoiceLink,
    '{portal_link}':   portalLink,
    '{intake_link}':   intakeLink,
    // timing
    '{days_since_inquiry}': daysSinceInquiry,
    '{due_date}':           dueDatePretty,
  }
}

/**
 * Substitute {token}s in `str` using the values map. Unresolved tokens
 * are stripped to empty string rather than left as literals.
 */
export function resolveMergeTokens(str, values) {
  if (!str) return str
  return String(str).replace(/\{([a-z_]+)\}/g, (m, key) => {
    const full = `{${key}}`
    return Object.prototype.hasOwnProperty.call(values, full) ? values[full] : ''
  })
}
